import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconInfo } from './Icons';

/** Distância entre o "i" e o balão. */
const TIP_GAP = 6;
/** Meia largura máxima do balão (.info-tip) mais a margem da tela: o balão não sai pela borda. */
const TIP_HALF = 150 + 8;
/** Tempo para o mouse sair do "i" e chegar ao balão sem que ele feche. */
const CLOSE_DELAY = 200;

/** Um bloco do balão: as linhas, com um título opcional; os blocos vêm separados por um fio. */
export interface InfoGroup {
  title?: string;
  lines: string[];
}

interface InfoTipProps {
  /** O nome do botão para o leitor de tela (ex.: "Detalhes de Fulano"). */
  label: string;
  groups: InfoGroup[];
}

/** O mesmo texto do balão, corrido, para o leitor de tela: o título com dois-pontos, cada linha com ponto. */
const spoken = (groups: InfoGroup[]): string =>
  groups
    .flatMap((group) => [...(group.title ? [`${group.title}:`] : []), ...group.lines])
    .map((line) => (/[.:!?]$/.test(line) ? line : `${line}.`))
    .join(' ');

/**
 * Um "i" que guarda o detalhe fora da vista. O mouse e o foco pelo teclado
 * mostram o balão, e o mouse pode passar para cima dele; no celular não há
 * hover, então o toque abre e fecha, e tocar fora ou o Esc também fecham.
 * O leitor de tela não depende do balão: o mesmo texto é a descrição do botão.
 */
export function InfoTip({ label, groups }: InfoTipProps) {
  const button = useRef<HTMLButtonElement>(null);
  const tip = useRef<HTMLDivElement>(null);
  const pointer = useRef('');
  const closing = useRef<number>();
  const [at, setAt] = useState<{ left: number; top: number } | null>(null);
  const id = useId();
  const open = at !== null;

  // O balão vai para o body, centrado no "i": um absoluto seria cortado por quem rola.
  const position = () => {
    const box = button.current?.getBoundingClientRect();
    if (!box) return null;
    const center = box.left + box.width / 2;
    return { left: Math.min(Math.max(center, TIP_HALF), window.innerWidth - TIP_HALF), top: box.bottom + TIP_GAP };
  };
  const show = () => {
    window.clearTimeout(closing.current);
    setAt(position());
  };
  const hide = () => {
    window.clearTimeout(closing.current);
    setAt(null);
  };
  const hideSoon = () => {
    window.clearTimeout(closing.current);
    closing.current = window.setTimeout(() => setAt(null), CLOSE_DELAY);
  };

  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!button.current?.contains(target) && !tip.current?.contains(target)) hide();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide();
    };
    // Rolar ou girar a tela leva o balão junto com o "i", em vez de fechá-lo.
    const follow = () => setAt((current) => (current ? position() : current));
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    window.addEventListener('scroll', follow, true);
    window.addEventListener('resize', follow);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
      window.removeEventListener('scroll', follow, true);
      window.removeEventListener('resize', follow);
    };
  }, [open]);

  useEffect(() => () => window.clearTimeout(closing.current), []);

  return (
    <>
      <button
        ref={button}
        type="button"
        className="info-btn"
        aria-label={label}
        aria-describedby={id}
        data-open={open || undefined}
        onPointerDown={(event) => { pointer.current = event.pointerType; }}
        onPointerEnter={(event) => event.pointerType === 'mouse' && show()}
        onPointerLeave={(event) => event.pointerType === 'mouse' && hideSoon()}
        // Só o foco do teclado abre: o do toque viria antes do clique e o fecharia em seguida.
        onFocus={(event) => event.currentTarget.matches(':focus-visible') && show()}
        onBlur={hide}
        onClick={() => {
          // No mouse, o hover já abriu e o clique mantém; no toque e no teclado, o clique alterna.
          const mouse = pointer.current === 'mouse';
          pointer.current = '';
          if (mouse || !open) show();
          else hide();
        }}
      >
        <IconInfo />
      </button>
      <span id={id} className="sr-only">{spoken(groups)}</span>
      {at ? createPortal(
        <div className="info-tip-anchor" style={{ left: at.left, top: at.top }}>
          {/* Clicar no balão não tira o foco do "i" (o que o fecharia). */}
          <div
            ref={tip}
            className="info-tip"
            aria-hidden="true"
            onPointerEnter={(event) => event.pointerType === 'mouse' && show()}
            onPointerLeave={(event) => event.pointerType === 'mouse' && hideSoon()}
            onMouseDown={(event) => event.preventDefault()}
          >
            {groups.map((group, index) => (
              <div key={group.title ?? index} className="info-tip-group">
                {group.title ? <div className="info-tip-title">{group.title}</div> : null}
                <ul>
                  {group.lines.map((line) => <li key={line}>{line}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>,
        document.body,
      ) : null}
    </>
  );
}
